import { useEffect, useState } from "react";
import { Factory, Plus, Edit2, Trash2, X, Loader2, Tags, ShoppingCart, Search } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useToast } from "@/lib/toast";
import { useAuth } from "@/lib/auth";
import { PageHeader, Empty, Field, Spinner } from "@/components/UI";
import { formatCurrency, formatDate } from "@/lib/format";

const EMPTY = { name: "", contact_person: "", phone: "", email: "", address: "", tin: "", payment_terms: "", notes: "", supplied_items: [] };

const RelatedItemsSelect = ({ value, onChange }) => {
  const [products, setProducts] = useState(null);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);

  useEffect(() => {
    api.listProducts().then(setProducts).catch(() => setProducts([]));
  }, []);

  const selected = value || [];
  const selectedIds = new Set(selected.map((x) => x.product_id));
  const ql = q.trim().toLowerCase();
  const groups = {};
  (products || []).forEach((p) => {
    if (selectedIds.has(p.id)) return;
    if (ql && !(p.name || "").toLowerCase().includes(ql) && !(p.category || "").toLowerCase().includes(ql)) return;
    (groups[p.category || "Uncategorised"] ||= []).push(p);
  });

  const add = (p) => {
    onChange([...selected, { product_id: p.id, product_name: p.name, category: p.category || "" }]);
    setQ("");
  };
  const remove = (pid) => onChange(selected.filter((x) => x.product_id !== pid));

  return (
    <div data-testid="related-items-select">
      {selected.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-2">
          {selected.map((it) => (
            <span key={it.product_id} data-testid={`related-chip-${it.product_id}`}
              className="inline-flex items-center gap-1 bg-emerald-50 text-emerald-800 border border-emerald-200 rounded-full px-2 py-0.5 text-xs font-medium">
              {it.product_name}
              <button type="button" data-testid={`related-remove-${it.product_id}`}
                className="hover:text-red-600" onClick={() => remove(it.product_id)}>
                <X size={11} />
              </button>
            </span>
          ))}
        </div>
      )}
      <div className="relative">
        <input
          data-testid="related-items-search"
          className="input"
          placeholder={products === null ? "Loading products…" : "Search products to link…"}
          value={q}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
        />
        {open && products !== null && (
          <div className="absolute z-50 mt-1 w-full bg-white border border-slate-200 rounded-lg shadow-xl max-h-52 overflow-y-auto" data-testid="related-items-dropdown">
            {Object.keys(groups).length === 0 ? (
              <div className="px-3 py-2 text-xs muted">
                {products.length === 0 ? "No products in your price list yet" : "No matches (or already linked)"}
              </div>
            ) : (
              Object.entries(groups).map(([cat, list]) => (
                <div key={cat}>
                  <div className="px-3 py-1 text-[10px] uppercase tracking-wider font-bold text-slate-400 bg-slate-50 sticky top-0">{cat}</div>
                  {list.map((p) => (
                    <button key={p.id} type="button" data-testid={`related-option-${p.id}`}
                      className="w-full text-left px-3 py-1.5 text-xs hover:bg-emerald-50/60 border-t border-slate-50"
                      onMouseDown={(e) => { e.preventDefault(); add(p); }}>
                      {p.name}
                    </button>
                  ))}
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default function Suppliers() {
  const { toast } = useToast();
  const { user } = useAuth();
  const nav = useNavigate();
  const canManage = user?.role === "admin" || user?.role === "manager";
  const isAdmin = user?.role === "admin";
  const [list, setList] = useState(null);
  const [query, setQuery] = useState("");
  const [modal, setModal] = useState(null); // {id?, ...fields}
  const [saving, setSaving] = useState(false);
  const [detail, setDetail] = useState(null); // {supplier, prices|null}

  const load = () => api.listSuppliers().then(setList).catch(() => setList([]));
  useEffect(() => { load(); }, []);

  const q = query.trim().toLowerCase();
  const shown = (list || []).filter((s) =>
    !q || [s.name, s.contact_person, s.email, s.phone].some((v) => (v || "").toLowerCase().includes(q)));

  const save = async () => {
    if (!modal.name.trim()) { toast("Supplier name is required", "error"); return; }
    setSaving(true);
    try {
      if (modal.id) { await api.updateSupplier(modal.id, modal); toast("Supplier updated", "success"); }
      else { await api.createSupplier(modal); toast("Supplier added", "success"); }
      setModal(null); load();
    } catch (e) { toast(e?.response?.data?.detail || "Save failed", "error"); }
    setSaving(false);
  };

  const remove = async (s) => {
    if (!window.confirm(`Delete supplier "${s.name}" and their price list?`)) return;
    try { await api.deleteSupplier(s.id); toast("Supplier deleted", "success"); load(); }
    catch (e) { toast(e?.response?.data?.detail || "Delete failed", "error"); }
  };

  const openDetail = async (s) => {
    setDetail({ supplier: s, prices: null });
    try { setDetail({ supplier: s, prices: await api.supplierPrices(s.id) }); }
    catch { setDetail({ supplier: s, prices: [] }); }
  };

  if (!list) return <div className="empty"><Spinner /> &nbsp;Loading…</div>;

  return (
    <div data-testid="page-suppliers">
      <PageHeader
        icon={Factory}
        title="Suppliers"
        subtitle="Supplier directory & price lists — fed automatically by every LPO you issue"
        action={canManage && (
          <button className="btn btn-primary" data-testid="new-supplier-btn" onClick={() => setModal({ ...EMPTY })}>
            <Plus size={14} /> New Supplier
          </button>
        )}
      />

      {list.length > 0 && (
        <div className="mb-3 relative max-w-sm">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
          <input data-testid="supplier-search" className="input !pl-9" placeholder="Search suppliers…"
            value={query} onChange={(e) => setQuery(e.target.value)} />
        </div>
      )}

      <div className="card overflow-hidden">
        {list.length === 0 ? (
          <Empty icon={Factory} title="No suppliers yet"
            description="Add your suppliers/contractors — their prices build up automatically from LPOs."
            action={canManage && (
              <button className="btn btn-primary" onClick={() => setModal({ ...EMPTY })}>
                <Plus size={14} /> Add your first supplier
              </button>
            )} />
        ) : (
          <div className="overflow-x-auto">
            <table className="tbl">
              <thead>
                <tr>
                  <th>Supplier</th><th>Contact</th><th>Phone</th><th>Email</th>
                  <th>TIN</th><th>Payment terms</th>
                  <th style={{ textAlign: "right" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {shown.map((s) => (
                  <tr key={s.id} data-testid={`supplier-row-${s.id}`}>
                    <td>
                      <button className="font-semibold hover:text-[#0e6939] text-left" data-testid={`supplier-open-${s.id}`}
                        onClick={() => openDetail(s)}>
                        {s.name}
                      </button>
                    </td>
                    <td className="muted">{s.contact_person || "—"}</td>
                    <td className="muted">{s.phone || "—"}</td>
                    <td className="muted">{s.email || "—"}</td>
                    <td className="muted">{s.tin || "—"}</td>
                    <td className="muted text-xs">{s.payment_terms || "—"}</td>
                    <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                      <button className="text-slate-500 hover:text-[#0e6939] mr-3" title="Price list"
                        data-testid={`supplier-prices-${s.id}`} onClick={() => openDetail(s)}>
                        <Tags size={14} />
                      </button>
                      <button className="text-slate-500 hover:text-[#0e6939] mr-3" title="Issue LPO to this supplier"
                        data-testid={`supplier-lpo-${s.id}`} onClick={() => nav(`/dms/lpos?new=1&supplier=${s.id}`)}>
                        <ShoppingCart size={14} />
                      </button>
                      {canManage && (
                        <button className="text-slate-500 hover:text-[#0e6939] mr-3" data-testid={`supplier-edit-${s.id}`}
                          onClick={() => setModal({ ...EMPTY, ...s })}>
                          <Edit2 size={14} />
                        </button>
                      )}
                      {isAdmin && (
                        <button className="text-slate-400 hover:text-red-600" data-testid={`supplier-del-${s.id}`}
                          onClick={() => remove(s)}>
                          <Trash2 size={14} />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {modal && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => !saving && setModal(null)}>
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="supplier-modal">
            <div className="flex items-center justify-between px-5 py-3 border-b border-slate-100">
              <div className="font-bold text-sm">{modal.id ? "Edit supplier" : "New supplier"}</div>
              <button onClick={() => setModal(null)}><X size={16} className="text-slate-400" /></button>
            </div>
            <div className="p-5 space-y-3 overflow-y-auto flex-1 min-h-0">
              <Field label="Company / supplier name *">
                <input data-testid="supplier-name" className="input" value={modal.name}
                  onChange={(e) => setModal({ ...modal, name: e.target.value })} />
              </Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Contact person">
                  <input data-testid="supplier-contact" className="input" value={modal.contact_person}
                    onChange={(e) => setModal({ ...modal, contact_person: e.target.value })} />
                </Field>
                <Field label="Phone">
                  <input data-testid="supplier-phone" className="input" value={modal.phone}
                    onChange={(e) => setModal({ ...modal, phone: e.target.value })} />
                </Field>
                <Field label="Email">
                  <input data-testid="supplier-email" className="input" value={modal.email}
                    onChange={(e) => setModal({ ...modal, email: e.target.value })} />
                </Field>
                <Field label="TIN">
                  <input data-testid="supplier-tin" className="input" value={modal.tin}
                    onChange={(e) => setModal({ ...modal, tin: e.target.value })} />
                </Field>
              </div>
              <Field label="Address">
                <input data-testid="supplier-address" className="input" value={modal.address}
                  onChange={(e) => setModal({ ...modal, address: e.target.value })} />
              </Field>
              <Field label="Payment terms">
                <input data-testid="supplier-terms" className="input" placeholder="e.g. 30 days credit / Cash on delivery"
                  value={modal.payment_terms} onChange={(e) => setModal({ ...modal, payment_terms: e.target.value })} />
              </Field>
              <Field label="Related items (from your price list)">
                <RelatedItemsSelect
                  value={modal.supplied_items}
                  onChange={(items) => setModal({ ...modal, supplied_items: items })}
                />
              </Field>
              <Field label="Notes">
                <input data-testid="supplier-notes" className="input" value={modal.notes}
                  onChange={(e) => setModal({ ...modal, notes: e.target.value })} />
              </Field>
            </div>
            <div className="px-5 py-3 border-t border-slate-100 flex justify-end gap-2">
              <button className="btn btn-ghost" onClick={() => setModal(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={save} disabled={saving} data-testid="supplier-save">
                {saving ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} {modal.id ? "Save changes" : "Add supplier"}
              </button>
            </div>
          </div>
        </div>
      )}

      {detail && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setDetail(null)}>
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="supplier-detail">
            <div className="flex items-center justify-between px-5 py-3 border-b border-slate-100">
              <div>
                <div className="font-bold text-sm">{detail.supplier.name}</div>
                <div className="text-xs muted">
                  {[detail.supplier.contact_person, detail.supplier.phone, detail.supplier.email].filter(Boolean).join(" · ") || "No contact details"}
                </div>
              </div>
              <button onClick={() => setDetail(null)}><X size={16} className="text-slate-400" /></button>
            </div>
            <div className="p-5 overflow-y-auto flex-1 min-h-0">
              {(detail.supplier.supplied_items || []).length > 0 && (
                <div className="mb-4">
                  <div className="font-bold text-xs uppercase tracking-wider muted mb-2">Related items</div>
                  <div className="flex flex-wrap gap-1.5" data-testid="detail-related-items">
                    {detail.supplier.supplied_items.map((it) => (
                      <span key={it.product_id} className="bg-slate-100 text-slate-700 rounded-full px-2 py-0.5 text-xs font-medium">
                        {it.product_name}{it.category ? ` · ${it.category}` : ""}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              <div className="font-bold text-xs uppercase tracking-wider muted mb-2 flex items-center gap-1.5">
                <Tags size={12} /> Price list (from LPOs)
              </div>
              {detail.prices === null ? (
                <div className="py-6 text-center"><Loader2 className="animate-spin inline" /></div>
              ) : detail.prices.length === 0 ? (
                <div className="text-sm muted py-4" data-testid="supplier-no-prices">
                  No prices yet — issue an LPO to this supplier and every item's price is saved here automatically.
                </div>
              ) : (
                <table className="tbl">
                  <thead>
                    <tr><th>Item</th><th>UoM</th><th style={{ textAlign: "right" }}>Last price</th><th>Last LPO</th><th>Last bought</th><th style={{ textAlign: "right" }}>Times</th></tr>
                  </thead>
                  <tbody>
                    {detail.prices.map((p) => (
                      <tr key={p.id} data-testid={`supplier-price-${p.id}`}>
                        <td className="font-medium">{p.description}</td>
                        <td className="muted">{p.unit_of_measure}</td>
                        <td style={{ textAlign: "right" }} className="font-semibold">{formatCurrency(p.cost_price, p.currency)}</td>
                        <td className="font-mono text-xs">{p.last_lpo_serial || "—"}</td>
                        <td className="muted text-xs">{formatDate(p.last_bought_at)}</td>
                        <td style={{ textAlign: "right" }} className="muted">{p.times_bought || 1}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
            <div className="px-5 py-3 border-t border-slate-100 flex justify-end">
              <button className="btn btn-primary" data-testid="supplier-detail-lpo"
                onClick={() => nav(`/dms/lpos?new=1&supplier=${detail.supplier.id}`)}>
                <ShoppingCart size={14} /> Issue LPO to {detail.supplier.name.split(" ")[0]}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
